import { AreaTypes } from "@/helpers/enums"
import MarkerUtils from "@/helpers/markerUtils"
import AreaUtils from "@/helpers/areaUtils"
import ApiDataModel, { GeometryData } from "@/models/apidatamodel"
import L from "leaflet"
import moment from "moment"
import DynamicRightSlotData from "@/models/dynamicrighslotdata"
import TableData from "@/models/tabledata"
import { store } from '@/store/store'
import i18n from '@/i18n'

const t = i18n.global.t

export interface PostiToimipiste {
    readonly name: string;
    readonly languageTerm: string;
    readonly dataTyypit: number[];
}

export interface Postipalvelut {
    id: number;
    tyyppi: number;
    julkaisuaika: string;
    nimi: string;
    katuosoite: string | null;
    postinro: string | null;
    info: string | null;

    koordinaatit: GeometryData;
    popup: string;
}

export interface PostipalveluMaarat {
    nimi: string | null;
    namn: string | null;
    valmistumisaika: string | null;
    kuntanumero: string | null;
    maakuntanumero: string | null;
    geoms: GeometryData | null;

    rightSlotData?: DynamicRightSlotData | null
    postitoimipisteet: number;
    pakettiautomaatit: number;
    noutopisteet: number;
    kirjelaatikot: number;
    postilokerot: number;
    yrityspisteet: number;
    julkaisuaika: string;
}

export default class PostiService {
    markerUtils = new MarkerUtils(store.getters.contentServiceUrl, store.getters.contentServiceTimeStamp)
    AreaUtils = new AreaUtils()
    apiCallBasePart: string = "v1/Viestinta/Posti/Postipalvelut/"
    selectedRadioButton: string = ""

    get currentLanguage() {
        return store.getters.activeLang
    }

    /** Haetun datan käsittelyä: Postipalvelut
     eri värisiä markereita riippuen Tyypistä / legenda näytettävä / tooltip tyypin mukaan
     rightslotissa näytetään koska tiedot on päivitetty sekä kartalla näkyvien toimipisteiden määrä, käsitellään map.vue ja piirron lopuksi
     */
    async handleDataPosti(data): Promise<ApiDataModel[]> {
        try {
            const apiData = new Array<ApiDataModel>()

            data.forEach((value) => {
                const tyyppi = this.getMonitoriTyyppi(value.tyyppi)
                if (tyyppi) {
                    const markerOptions = {
                        id: value.id,
                        type: tyyppi,
                        riseOnHover: true,
                        icon: L.icon({
                            iconUrl: this.getToimipaikkaTyyppiIcon(tyyppi),
                            iconAnchor: [12, 40],
                            iconSize: [24, 40],
                            popupAnchor: [0, -30]
                        })
                    }
                    value.koordinaatit.markerOptions = markerOptions
                }
                value.koordinaatit.handleOnClick = true
                value.popup = " "

                apiData.push(new ApiDataModel(value.id.toString(), true, value.koordinaatit, value, null, null))
            })

            if (data.length > 0) {
                // Tiedot päivitetty teksti
                const rightSlotData = new DynamicRightSlotData(data[0].id.toString())
                rightSlotData.dataUpdatedDate = this.getUpdatedtext(data[0].julkaisuaika ?? moment().toISOString()) ?? "?"
                store.dispatch("setRightSlotData", rightSlotData)
            }

            return apiData
        }
        catch (err) {
            return new Array<ApiDataModel>()
        }
    }

    async createPopuptext(data): Promise<string> {
        const days: { name: string, value: string | undefined }[] = [
            { name: "ma", value: t("App.Posti.Popup.Availability.Monday") },
            { name: "ti", value: t("App.Posti.Popup.Availability.Tuesday") },
            { name: "ke", value: t("App.Posti.Popup.Availability.Wednesday") },
            { name: "to", value: t("App.Posti.Popup.Availability.Thursday") },
            { name: "pe", value: t("App.Posti.Popup.Availability.Friday") },
            { name: "la", value: t("App.Posti.Popup.Availability.Saturday") },
            { name: "su", value: t("App.Posti.Popup.Availability.Sunday") }
        ]

        let texts = ""

        for (let i = 0; i < data.length; i++) {
            const tyyppi = this.getMonitoriTyyppi(data[i].tyyppi)

            const toimipisteet = this.getPostitoimipisteet()
            let tyyppiString = ""
            for (let i = 0; i < toimipisteet.length; i++) {
                if (tyyppi === toimipisteet[i].name) {
                    tyyppiString = toimipisteet[i].languageTerm
                }
            }

            let popup: string = ""

            if (data[i].info) {
                const info = data[i].info
                const lang = this.currentLanguage
                let address = null

                if (info.address[lang] != null) {
                    address = info.address[lang].address
                }

                if (address !== null) {
                    popup += "<p><b>" + t("App.Posti.Popup." + tyyppiString) + "</b> " + address + "</p>"
                }
                else {
                    popup += "<p><b>" + t("App.Posti.Popup." + tyyppiString) + "</b> " + data[i].katuosoite + "</p>"
                }

                popup += "<p><b>" + t("App.Posti.Popup.Avoinna") + ": </b>" + this.getTranslatedDays(info.availability, days) + "</p>"

                if (info.dropOffTimeLetters !== null || info.dropOffTimeExpress !== null || info.dropOffTimeParcel !== null) {
                    popup += "<p><b>" + t("App.Posti.Popup.ViimeisetJattoajat") + ": </b>"

                    if (info.dropOffTimeLetters !== null) {
                        popup += "<div>" + t("App.Posti.Popup.DropOffTimes.Letters") + " " + this.formatTime(info.dropOffTimeLetters) + ", </div>"
                    }
                    if (info.dropOffTimeExpress !== null) {
                        popup += "<div>" + t("App.Posti.Popup.DropOffTimes.Express") + " " + this.formatTime(info.dropOffTimeExpress) + ", </div>"
                    }
                    if (info.dropOffTimeParcel !== null) {
                        popup += "<div>" + t("App.Posti.Popup.DropOffTimes.Parcel") + " " + this.formatTime(info.dropOffTimeParcel) + "</div>"
                    }

                    popup += "</p>"
                }
            }
            data[i].popup = popup
            if (data.length > 1) {
                texts += "<section class=\"popup-item\">" + data[i].popup + "</section>"
            }
            else {
                texts += data[i].popup
            }
        }
        return texts
    }

    formatTime(time: any): string {
        const parts = time.split(":")
        if (parts.length !== 3) {
            return time.toString()
        }
        return parts[0] + "." + parts[1]
    }

    getMonitoriTyyppi(dataTyyppi: number): string | undefined {
        const toimipisteet = this.getPostitoimipisteet()
        for (let i = 0; i < toimipisteet.length; i++) {
            if (toimipisteet[i].dataTyypit.includes(dataTyyppi)) {
                return toimipisteet[i].name
            }
        }
    }

    getDataTyyppi(monitoritTyppi: string): number[] {
        const toimipisteet = this.getPostitoimipisteet()
        for (let i = 0; i < toimipisteet.length; i++) {
            if (toimipisteet[i].name === monitoritTyppi) {
                return toimipisteet[i].dataTyypit
            }
        }
        return []
    }

    getToimipaikkaTyyppiIcon(tyyppi: string) {
        if (tyyppi === "postitoimipiste") {
            return this.markerUtils.createDarkRedMarker()
        }
        else if (tyyppi === "noutopiste") {
            return this.markerUtils.createBlueMarker()
        }
        else if (tyyppi === "postilokero") {
            return this.markerUtils.createGrayMarker()
        }
        else if (tyyppi === "pakettiautomaatti") {
            return this.markerUtils.createTurquoiseMarker()
        }
        else if (tyyppi === "kirjelaatikko") {
            return this.markerUtils.createPurpleMarker()
        }
        else if (tyyppi === "yrityspiste") {
            return this.markerUtils.createBlackMarker()
        }
        else {
            return this.markerUtils.createBlueMarker()
        }
    }

    getToimipaikkaTyyppiLegend(): string {
        let legend = ""
        const toimipisteet = this.getPostitoimipisteet()
        for (let i = 0; i < toimipisteet.length; i++) {
            legend += "<div class=\"legend-item\"><img alt=\"\" src= " + this.getToimipaikkaTyyppiIcon(toimipisteet[i].name) + "\">" + t("App.Posti.CheckboxList." + toimipisteet[i].languageTerm) + "</div>"
        }
        return legend
    }

    getTranslatedDays(value: string, days: any): string {
        if (!value) {
            return ""
        }
        else {
            for (let i = 0; i < days.length; i++) {
                // Palautetaan käännetty teksti samassa casessa kuin parametri
                if (value === value.toUpperCase()) {
                    value = value.toLowerCase().replace(days[i].name, days[i].value).toUpperCase()
                }
                else {
                    value = value.replace(days[i].name, days[i].value)
                }
            }
            return value
        }
    }

    getTyyppiLkm(): number {
        return this.getPostitoimipisteet().length
    }

    getPostitoimipisteet(): PostiToimipiste[] {
        return [
            { name: "postitoimipiste", languageTerm: "PostOffice", dataTyypit: [1, 2] },
            { name: "noutopiste", languageTerm: "MailPickUpPoint", dataTyypit: [4] },
            { name: "postilokero", languageTerm: "POBox", dataTyypit: [9] },
            { name: "pakettiautomaatti", languageTerm: "ParcelLocker", dataTyypit: [3] },
            { name: "kirjelaatikko", languageTerm: "LetterBox", dataTyypit: [6, 7, 8] },
            { name: "yrityspiste", languageTerm: "CorporateServicePoint", dataTyypit: [5] }
        ]
    }

    getUpdatedtext(time: string): string {
        let updatedTime = "?"
        if (moment(time).format("MM/YYYY")) {
            updatedTime = t("App.Posti.DataUpdated") + " " + moment(time).format("MM/YYYY")
        }
        return updatedTime
    }

    // #region PostiMaarat
    async handleDataPostiMaarat(data): Promise<ApiDataModel[]> {
        try {
            const areaType = this.AreaUtils.getAreaTypeFromData(data)
            // Haetaan ensin kuntien/maakuntien rajat storesta
            const models = this.AreaUtils.getAreas(areaType, false)

            // Lisätään sitten RightSlotData
            data.forEach((value) => {
                let id: string = "0"
                if (areaType === AreaTypes.Municipality) {
                    id = value.kuntanumero ?? "0"
                }
                else if (areaType === AreaTypes.Province) {
                    id = value.maakuntanumero ?? "0"
                }

                const rightSlotData = new DynamicRightSlotData(id)
                if (areaType === AreaTypes.Country) {
                    rightSlotData.municipality = t("App.Posti.EntireFinland") ?? ""
                }
                else {
                    rightSlotData.municipality = this.getName(value)
                }
                rightSlotData.dataUpdatedDate = this.getUpdatedtext(value.julkaisuaika ?? moment().toISOString()) ?? "?"

                rightSlotData.tableData = this.getServiceTypesTable(value)

                if (areaType === AreaTypes.Country) {
                    store.dispatch("setRightSlotData", rightSlotData)
                }

                // Päivitetään tiedot modeleihin
                this.updateModels(models, rightSlotData, "", id)
            })
            return models
        }
        catch (err) {
            return new Array<ApiDataModel>()
        }
    }

    updateModels(models: ApiDataModel[], rightSlotData: DynamicRightSlotData, color: string, id: string) {
        const item = models.find(e => e.id === id)
        if (item !== undefined) {
            item.rightSlotData = rightSlotData
            if (item.geometryData !== null) {
                // Vain ne alueet voi valita kartalta, joilla on dataa
                item.geometryData.handleOnClick = true
            }
        }
    }

    /** Haetaan valitun kielen mukaan oikea nimi */
    getName(value) {
        let newName: string = ""

        switch (this.currentLanguage) {
            case "fi":
                newName = value.nimi ?? "-"
                break
            case "sv":
                newName = value.namn ?? "-"
                break
            case "en":
                newName = value.nimi ?? "-"
                break
            default:
                newName = value.nimi ?? "-"
                break
        }

        return newName
    }

    /** Haetaan valitun kielen mukaan oikea linkki */
    getUrl(value) {
        let newUrl: string = ""

        switch (this.currentLanguage) {
            case "fi":
                newUrl = value.urlfi ?? "#"
                break
            case "sv":
                newUrl = value.urlsv ?? "#"
                break
            case "en":
                newUrl = value.urlen ?? "#"
                break
            default:
                newUrl = value.urlfi ?? "#"
                break
        }

        return newUrl
    }

    getServiceTypesTable(value: PostipalveluMaarat): TableData {
        return new TableData([t("App.Posti.Palvelupiste") ?? "", t("App.Posti.Maara") ?? ""], [
            [t("App.Posti.Postitoimipisteet") ?? "", value.postitoimipisteet.toString()],
            [t("App.Posti.Pakettiautomaatit") ?? "", value.pakettiautomaatit.toString()],
            [t("App.Posti.Noutopisteet") ?? "", value.noutopisteet.toString()],
            [t("App.Posti.Kirjelaatikot") ?? "", value.kirjelaatikot.toString()],
            [t("App.Posti.Postilokerot") ?? "", value.postilokerot.toString()],
            [t("App.Posti.Yrityspisteet") ?? "", value.yrityspisteet.toString()]
        ])
    }

    get modalHeader(): string {
        return store.getters.header
    }

    // #endregion
}
